/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Plus, Trash2, Download, FileText, RefreshCw, ChevronDown, ChevronUp, Receipt, Mail } from "lucide-react";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Badge } from "./components/ui/badge";
import { toast } from "sonner";
import { FORMATIONS } from "./data/formations";
import { DatePickerFr } from "./components/DatePickerFr";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const adminHeaders = () => ({ "X-Admin-Password": sessionStorage.getItem("adminPassword") || "" });
const selectCls = "w-full h-10 rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#003087]/30";
const eur = (v) => `${Number(v || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €`;
const frDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR") : "—");

const downloadWithAuth = async (url, filename) => {
  try {
    const res = await axios.get(url, { headers: adminHeaders(), responseType: "blob" });
    const cd = res.headers["content-disposition"] || "";
    const match = cd.match(/filename="?([^";]+)"?/);
    const blobUrl = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = match ? match[1] : filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
  } catch {
    toast.error("Téléchargement impossible");
  }
};

const usePilotageConfig = () => {
  const [config, setConfig] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/pilotage/config`, { headers: adminHeaders() }).then((r) => setConfig(r.data)).catch(() => toast.error("Config pilotage indisponible"));
  }, []);
  return config;
};

// ==================================================================
// DOSSIERS — parcours administratif Entreprise / Particulier
// ==================================================================
const EMPTY_FORM = {
  type_client: "particulier", prenom: "", nom: "", email: "", entreprise: "", adresse_client: "", siret_client: "",
  formation_code: "", formation_title: "", hours: 0, price_ht: 0, financement: "individuel",
  statut_stagiaire: "particulier", modalite: "mixte", date_debut: "", date_fin: "", notes: "",
};

const DossierForm = ({ config, onCreated }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickFormation = (code) => {
    const f = FORMATIONS.find((x) => x.code === code);
    setForm((prev) => ({
      ...prev, formation_code: code, formation_title: f?.title || "", hours: f?.hours || 0, price_ht: f?.price || 0,
      modalite: f?.modality === "Présentiel" ? "presentiel" : f?.modality === "Distanciel" ? "distanciel" : "mixte",
    }));
  };
  const pickType = (t) => setForm((prev) => ({
    ...prev, type_client: t,
    financement: t === "entreprise" ? "entreprise" : "individuel",
    statut_stagiaire: t === "entreprise" ? "salarie" : "particulier",
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.formation_code) return toast.error("Choisissez une formation");
    setSaving(true);
    try {
      const payload = { ...form, hours: Number(form.hours), price_ht: Number(form.price_ht), date_debut: form.date_debut || null, date_fin: form.date_fin || null };
      const res = await axios.post(`${API}/admin/dossiers`, payload, { headers: adminHeaders() });
      onCreated(res.data);
      setForm(EMPTY_FORM);
      toast.success("Dossier créé");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Création impossible");
    } finally {
      setSaving(false);
    }
  };

  const isPro = form.type_client === "entreprise";
  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="dossier-form">
      <div>
        <Label>Type de client</Label>
        <select className={selectCls} value={form.type_client} onChange={(e) => pickType(e.target.value)} data-testid="dossier-type-client">
          <option value="particulier">Particulier (personne physique)</option>
          <option value="entreprise">Entreprise (professionnel)</option>
        </select>
      </div>
      <div><Label>Prénom</Label><Input required value={form.prenom} onChange={(e) => set("prenom", e.target.value)} data-testid="dossier-prenom" /></div>
      <div><Label>Nom</Label><Input required value={form.nom} onChange={(e) => set("nom", e.target.value)} data-testid="dossier-nom" /></div>
      <div><Label>E-mail</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="dossier-email" /></div>
      {isPro && (
        <>
          <div><Label>Entreprise</Label><Input value={form.entreprise} onChange={(e) => set("entreprise", e.target.value)} data-testid="dossier-entreprise" /></div>
          <div><Label>SIRET client</Label><Input value={form.siret_client} onChange={(e) => set("siret_client", e.target.value)} data-testid="dossier-siret" /></div>
        </>
      )}
      <div className={isPro ? "md:col-span-3" : "md:col-span-2"}>
        <Label>Adresse de facturation</Label>
        <Input value={form.adresse_client} onChange={(e) => set("adresse_client", e.target.value)} data-testid="dossier-adresse" />
      </div>
      <div className="md:col-span-2">
        <Label>Formation</Label>
        <select className={selectCls} value={form.formation_code} onChange={(e) => pickFormation(e.target.value)} data-testid="dossier-formation">
          <option value="">— Choisir —</option>
          {FORMATIONS.map((f) => <option key={f.code} value={f.code}>{f.code} — {f.title} ({f.hours}h · {f.price}€)</option>)}
        </select>
      </div>
      <div><Label>Heures</Label><Input type="number" step="0.5" min="0" value={form.hours} onChange={(e) => set("hours", e.target.value)} data-testid="dossier-hours" /></div>
      <div><Label>Prix HT (€)</Label><Input type="number" step="0.01" min="0" value={form.price_ht} onChange={(e) => set("price_ht", e.target.value)} data-testid="dossier-price" /></div>
      <div>
        <Label>Financement</Label>
        <select className={selectCls} value={form.financement} onChange={(e) => set("financement", e.target.value)} data-testid="dossier-financement">
          {Object.entries(config.financements).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      <div>
        <Label>Statut du stagiaire</Label>
        <select className={selectCls} value={form.statut_stagiaire} onChange={(e) => set("statut_stagiaire", e.target.value)} data-testid="dossier-statut">
          {Object.entries(config.statuts).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      <div>
        <Label>Modalité</Label>
        <select className={selectCls} value={form.modalite} onChange={(e) => set("modalite", e.target.value)} data-testid="dossier-modalite">
          {Object.entries(config.modalites).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      <div><Label>Date de début</Label><DatePickerFr value={form.date_debut} onChange={(v) => set("date_debut", v)} data-testid="dossier-date-debut" /></div>
      <div><Label>Date de fin</Label><DatePickerFr value={form.date_fin} onChange={(v) => set("date_fin", v)} data-testid="dossier-date-fin" /></div>
      <div className="md:col-span-3"><Label>Notes internes</Label><Input value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="dossier-notes" /></div>
      <div className="md:col-span-3">
        <Button type="submit" disabled={saving} className="bg-[#F07B1D] hover:bg-[#D96A0F] text-white font-bold" data-testid="dossier-submit">
          <Plus className="h-4 w-4 mr-1" /> {saving ? "Création…" : "Créer le dossier"}
        </Button>
      </div>
    </form>
  );
};

const STEP_DOCS = {
  proposition: [["devis", "Devis"]],
  contractualisation: [["contrat", "Convention / Contrat"]],
  convocation: [["convocation", "Convocation + règlement intérieur"]],
  emargement: [["emargement", "Feuille d'émargement (PDF)"]],
  cloture: [["attestation", "Attestation de fin de formation"], ["certificat", "Certificat de réalisation"]],
  facturation: [["facture", "Facture"]],
};

const DocActions = ({ dossier, type, label, onDoc, onSend, sending }) => {
  const sentAt = dossier.documents?.[`${type}_sent_at`];
  return (
    <div className="flex items-center gap-1 flex-wrap">
      <Button size="sm" variant="outline" className="h-7 text-xs border-[#F07B1D] text-[#F07B1D] hover:bg-[#F07B1D] hover:text-white" onClick={() => onDoc(dossier, type)} data-testid={`doc-btn-${dossier.id}-${type}`}>
        <Download className="h-3 w-3 mr-1" /> {label}
      </Button>
      <Button size="sm" variant="outline" className="h-7 text-xs border-[#003087] text-[#003087] hover:bg-[#003087] hover:text-white" disabled={!dossier.email || sending === type} onClick={() => onSend(dossier, type)} title={dossier.email ? `Envoyer à ${dossier.email}` : "Aucun e-mail sur le dossier"} data-testid={`send-btn-${dossier.id}-${type}`}>
        <Mail className="h-3 w-3 mr-1" /> {sending === type ? "Envoi…" : "Envoyer"}
      </Button>
      {sentAt && <span className="text-[11px] text-green-700" data-testid={`sent-at-${dossier.id}-${type}`}>Envoyé le {new Date(sentAt).toLocaleDateString("fr-FR")}</span>}
    </div>
  );
};

const StepExtras = ({ dossier, stepKey, onAction }) => {
  if (stepKey === "emargement") {
    const slots = dossier.emargements || [];
    const signed = slots.filter((x) => x.signed_at).length;
    return (
      <p className="text-xs text-gray-600 mt-1" data-testid={`emargement-stats-${dossier.id}`}>
        {slots.length ? `${signed}/${slots.length} demi-journées signées par le stagiaire (espace apprenant)` : "Séances générées automatiquement à partir des dates du dossier lors de la 1re génération du PDF."}
      </p>
    );
  }
  if (stepKey === "evaluation" || stepKey === "suivi") {
    const kind = stepKey === "evaluation" ? "chaud" : "froid";
    const enq = dossier.enquetes?.[kind];
    return (
      <div className="flex flex-wrap items-center gap-2 mt-2" data-testid={`enquete-${kind}-${dossier.id}`}>
        <span className="text-xs text-gray-600">Enquête {kind === "chaud" ? "à chaud (envoi auto à la clôture)" : "à froid (envoi auto à J+30)"} :</span>
        {enq?.answered_at ? (
          <Badge className="bg-green-600 text-white">Répondue · {enq.moyenne ? `${enq.moyenne}/5` : "—"}{typeof enq.answers?.nps === "number" ? ` · NPS ${enq.answers.nps}/10` : ""}</Badge>
        ) : enq?.sent_at ? (
          <Badge variant="outline" className="border-amber-500 text-amber-700">Envoyée le {new Date(enq.sent_at).toLocaleDateString("fr-FR")} — en attente</Badge>
        ) : (
          <Button size="sm" variant="outline" className="h-7 text-xs border-[#003087] text-[#003087]" disabled={!dossier.email} onClick={() => onAction("enquete", kind)} data-testid={`enquete-send-${kind}-${dossier.id}`}>
            <Mail className="h-3 w-3 mr-1" /> Envoyer maintenant
          </Button>
        )}
      </div>
    );
  }
  if (stepKey === "facturation" && dossier.numero_facture) {
    const paid = dossier.paiement?.paid_at;
    const relances = dossier.relances || [];
    const overdue = !paid && dossier.date_echeance && new Date(dossier.date_echeance) < new Date();
    return (
      <div className="flex flex-wrap items-center gap-2 mt-2" data-testid={`paiement-${dossier.id}`}>
        {paid ? (
          <Badge className="bg-green-600 text-white">Réglée le {new Date(paid).toLocaleDateString("fr-FR")}</Badge>
        ) : (
          <Badge className={overdue ? "bg-red-600 text-white" : "bg-amber-500 text-white"}>{overdue ? "Impayée — échue" : `En attente · échéance ${new Date(dossier.date_echeance).toLocaleDateString("fr-FR")}`}</Badge>
        )}
        {relances.map((r) => <span key={r.sent_at} className="text-[11px] text-gray-600">Relance {r.niveau} le {new Date(r.sent_at).toLocaleDateString("fr-FR")}</span>)}
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onAction("paiement", !paid)} data-testid={`paiement-toggle-${dossier.id}`}>
          {paid ? "Annuler le règlement" : "Marquer réglée"}
        </Button>
        {!paid && (
          <Button size="sm" variant="outline" className="h-7 text-xs border-red-500 text-red-600" disabled={!dossier.email} onClick={() => onAction("relance")} data-testid={`relance-btn-${dossier.id}`}>
            <Mail className="h-3 w-3 mr-1" /> Relancer
          </Button>
        )}
        <span className="text-[11px] text-gray-500">Relances auto : à l'échéance puis J+15.</span>
      </div>
    );
  }
  return null;
};

const StepTimeline = ({ dossier, steps, onToggle, onDoc, onSend, sending, onAction }) => (
  <div className="space-y-1.5" data-testid={`steps-${dossier.id}`}>
    {steps.map((s, i) => {
      const st = dossier.steps?.[s.key] || {};
      return (
        <div key={s.key} className={`flex items-start gap-3 p-2.5 rounded-lg border ${st.done ? "bg-green-50 border-green-200" : "bg-white border-gray-200"}`}>
          <button
            type="button"
            onClick={() => onToggle(dossier.id, s.key, !st.done)}
            className={`mt-0.5 h-6 w-6 shrink-0 rounded-full border-2 text-xs font-bold flex items-center justify-center transition-colors ${st.done ? "bg-green-500 border-green-500 text-white" : "border-gray-300 text-gray-500 hover:border-[#F07B1D]"}`}
            data-testid={`step-toggle-${dossier.id}-${s.key}`}
            aria-label={`Basculer ${s.label}`}
          >
            {st.done ? "✓" : i + 1}
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-[#003087]">{i + 1}. {s.label} <span className="text-gray-500 font-normal">— {s.actions}</span></p>
            <p className="text-xs text-gray-600"><span className="font-semibold">Documents :</span> {s.documents}</p>
            {st.done && st.at && <p className="text-[11px] text-green-700 mt-0.5">Fait le {new Date(st.at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</p>}
            <StepExtras dossier={dossier} stepKey={s.key} onAction={(kind, arg) => onAction(dossier, kind, arg)} />
            {STEP_DOCS[s.key] && (
              <div className="flex flex-col gap-1.5 mt-2">
                {STEP_DOCS[s.key].map(([type, label]) => (
                  <DocActions key={type} dossier={dossier} type={type} label={label} onDoc={onDoc} onSend={onSend} sending={sending} />
                ))}
              </div>
            )}
          </div>
        </div>
      );
    })}
  </div>
);

const daysUntil = (iso) => (iso ? Math.ceil((new Date(iso) - new Date()) / 86400000) : null);

const DossierCard = ({ dossier, config, onToggle, onDelete, onDoc, onSend, sending, onAction }) => {
  const [open, setOpen] = useState(false);
  const steps = config.parcours[dossier.type_client] || config.parcours.particulier;
  const doneCount = steps.filter((s) => dossier.steps?.[s.key]?.done).length;
  const pct = Math.round((100 * doneCount) / steps.length);
  const days = daysUntil(dossier.date_debut);
  const convocationDue = days !== null && days <= 15 && days >= 0 && !dossier.documents?.convocation_sent_at;
  return (
    <div className={`border rounded-xl bg-white overflow-hidden ${convocationDue ? "border-[#F07B1D]" : "border-gray-200"}`} data-testid={`dossier-card-${dossier.id}`}>
      <div className="p-4 flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <Badge className={dossier.type_client === "entreprise" ? "bg-[#003087]" : "bg-[#2196F3]"}>{dossier.type_client === "entreprise" ? "Entreprise" : "Particulier"}</Badge>
            <p className="font-bold text-[#003087]">{dossier.prenom} {dossier.nom}{dossier.entreprise ? ` · ${dossier.entreprise}` : ""}</p>
            {dossier.numero_devis && <Badge variant="outline" className="border-[#003087] text-[#003087]">{dossier.numero_devis}</Badge>}
            {dossier.numero_facture && <Badge variant="outline" className="border-[#F07B1D] text-[#F07B1D]">{dossier.numero_facture}</Badge>}
            {convocationDue && (
              <Badge className="bg-[#F07B1D] text-white" data-testid={`convocation-due-${dossier.id}`}>
                Convocation à envoyer · J-{days}{days <= 15 && days >= 7 && dossier.email ? " (envoi auto programmé)" : ""}
              </Badge>
            )}
            {dossier.documents?.convocation_sent_at && <Badge className="bg-green-600 text-white">Convoqué</Badge>}
            {dossier.signatures?.devis && <Badge className="bg-emerald-600 text-white" data-testid={`signed-devis-${dossier.id}`}>Devis signé</Badge>}
            {!dossier.signatures?.devis && dossier.documents?.devis_sent_at && dossier.sign_token && !dossier.steps?.contractualisation?.done && (
              <Badge variant="outline" className="border-amber-500 text-amber-700" data-testid={`devis-attente-${dossier.id}`}>
                Devis en attente de signature · envoyé le {frDate(dossier.documents.devis_sent_at)}{dossier.relances_devis?.length ? ` · ${dossier.relances_devis.length} relance(s), dernière le ${frDate(dossier.relances_devis.at(-1).sent_at)}` : " · relance auto à J+3"}
              </Badge>
            )}
            {dossier.signatures?.contrat && <Badge className="bg-emerald-700 text-white" data-testid={`signed-contrat-${dossier.id}`}>{dossier.type_client === "entreprise" ? "Convention signée" : "Contrat signé"}</Badge>}
            {dossier.paiement?.acompte && <Badge className="bg-amber-500 text-white">Acompte reçu · solde {eur(dossier.paiement.solde_restant)}</Badge>}
            {dossier.source === "devis_web" && <Badge variant="outline" className="border-[#2196F3] text-[#2196F3]">Devis web</Badge>}
            {dossier.numero_facture && !dossier.paiement?.paid_at && dossier.date_echeance && new Date(dossier.date_echeance) < new Date() && <Badge className="bg-red-600 text-white" data-testid={`impaye-${dossier.id}`}>Impayée</Badge>}
          </div>
          <p className="text-sm text-gray-600 truncate">{dossier.formation_code} — {dossier.formation_title}</p>
          <p className="text-xs text-gray-500">{dossier.hours} h · {eur(dossier.price_ht)} HT · {config.financements[dossier.financement]} · {frDate(dossier.date_debut)} → {frDate(dossier.date_fin)}</p>
          <div className="mt-2 h-2 rounded-full bg-gray-100 overflow-hidden"><div className="h-full bg-[#F07B1D] transition-all" style={{ width: `${pct}%` }} /></div>
          <p className="text-[11px] text-gray-500 mt-1">{doneCount}/{steps.length} étapes · {pct}%</p>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <Button size="sm" variant="outline" className="border-[#003087] text-[#003087]" onClick={() => onDoc(dossier, "devis")} data-testid={`devis-btn-${dossier.id}`}>
            <FileText className="h-3.5 w-3.5 mr-1" /> Devis
          </Button>
          <Button size="sm" variant="outline" className="border-[#003087] text-[#003087]" onClick={() => onDoc(dossier, "facture")} data-testid={`facture-btn-${dossier.id}`}>
            <Receipt className="h-3.5 w-3.5 mr-1" /> Facture
          </Button>
          <Button size="sm" variant="outline" className="border-emerald-600 text-emerald-700" disabled={!dossier.email} onClick={() => onAction(dossier, "signature")} title="Envoyer le lien de signature électronique (devis puis contrat/convention)" data-testid={`signature-link-${dossier.id}`}>
            <Mail className="h-3.5 w-3.5 mr-1" /> Lien signature
          </Button>
          {!dossier.signatures?.devis && dossier.documents?.devis_sent_at && dossier.sign_token && (
            <Button size="sm" variant="outline" className="border-amber-500 text-amber-700" onClick={() => onAction(dossier, "relance-devis")} title="Renvoyer le devis avec le lien de signature" data-testid={`relance-devis-${dossier.id}`}>
              <Mail className="h-3.5 w-3.5 mr-1" /> Relancer devis
            </Button>
          )}
          <Button size="sm" variant="outline" className="border-[#F07B1D] text-[#F07B1D]" onClick={() => onAction(dossier, "paylink")} data-testid={`paylink-${dossier.id}`}>
            <Receipt className="h-3.5 w-3.5 mr-1" /> Lien paiement
          </Button>
          <Button size="sm" variant="outline" onClick={() => setOpen((o) => !o)} data-testid={`toggle-steps-${dossier.id}`}>
            {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />} Étapes
          </Button>
          <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => onDelete(dossier.id)} data-testid={`delete-dossier-${dossier.id}`}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      {open && <div className="px-4 pb-4"><StepTimeline dossier={dossier} steps={steps} onToggle={onToggle} onDoc={onDoc} onSend={onSend} sending={sending} onAction={onAction} /></div>}
    </div>
  );
};

const CommandesBlock = () => {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    axios.get(`${API}/admin/commandes`, { headers: adminHeaders() }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  if (!items) return null;
  const completed = items.filter((c) => c.status === "COMPLETED");
  return (
    <Card data-testid="commandes-block">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="text-base text-[#003087]">Commandes en ligne PayPal ({completed.length} payée{completed.length > 1 ? "s" : ""} · {eur(completed.reduce((a, c) => a + (c.paid_amount || c.amount || 0), 0))})</CardTitle>
            <CardDescription>Chaque paiement crée automatiquement un dossier stagiaire réglé avec facture.</CardDescription>
          </div>
          <Button size="sm" variant="outline" onClick={() => setOpen((o) => !o)} data-testid="commandes-toggle">{open ? "Masquer" : "Voir"}</Button>
        </div>
      </CardHeader>
      {open && (
        <CardContent>
          {items.length === 0 ? <p className="text-sm text-gray-500">Aucune commande.</p> : (
            <div className="space-y-1.5">
              {items.map((c) => (
                <div key={c.id} className="flex flex-col md:flex-row md:items-center gap-2 text-sm p-2 rounded-lg border border-gray-200" data-testid={`commande-${c.id}`}>
                  <Badge className={c.status === "COMPLETED" ? "bg-green-600 text-white" : "bg-gray-400 text-white"}>{c.status === "COMPLETED" ? "Payée" : c.status}</Badge>
                  <span className="text-gray-500">{new Date(c.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</span>
                  <span className="font-semibold text-[#003087]">{c.client?.prenom} {c.client?.nom}</span>
                  <span className="flex-1 truncate">{c.formation_code} — {c.formation_title}</span>
                  <span className="font-bold">{eur(c.amount)}</span>
                  {c.numero_facture && <Badge variant="outline" className="border-[#F07B1D] text-[#F07B1D]">{c.numero_facture}</Badge>}
                  <span className="text-[11px] text-gray-400">PayPal {c.paypal_order_id}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

const EMPTY_SESSION = { formation_code: "", date_debut: "", date_fin: "", horaires: "9h00 – 12h30 / 13h30 – 17h00", lieu: "MGB School — 25 rue de Ponthieu, 75008 Paris", places: 8, statut: "ouverte", notes: "" };

export const SessionsSection = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_SESSION);
  const [editing, setEditing] = useState(null);
  const presentiel = FORMATIONS.filter((f) => f.type === "presentiel");
  const load = useCallback(() => axios.get(`${API}/admin/sessions`, { headers: adminHeaders() }).then((r) => setItems(r.data)).catch(() => toast.error("Sessions indisponibles")), []);
  useEffect(() => { load(); }, [load]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, places: Number(form.places) };
      if (editing) await axios.put(`${API}/admin/sessions/${editing}`, payload, { headers: adminHeaders() });
      else await axios.post(`${API}/admin/sessions`, payload, { headers: adminHeaders() });
      toast.success(editing ? "Session mise à jour" : "Session créée");
      setForm(EMPTY_SESSION); setEditing(null); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Enregistrement impossible");
    }
  };
  const edit = (s) => { setEditing(s.id); setForm({ formation_code: s.formation_code, date_debut: s.date_debut, date_fin: s.date_fin, horaires: s.horaires, lieu: s.lieu, places: s.places, statut: s.statut === "complete" ? "ouverte" : s.statut, notes: s.notes || "" }); };
  const remove = async (id) => { if (!window.confirm("Supprimer cette session ?")) return; await axios.delete(`${API}/admin/sessions/${id}`, { headers: adminHeaders() }); load(); };
  return (
    <div className="space-y-6" data-testid="sessions-section">
      <Card>
        <CardHeader><CardTitle className="text-[#003087]">{editing ? "Modifier la session" : "Nouvelle session présentiel / mixte"}</CardTitle><CardDescription>Les sessions à venir s'affichent sur la fiche formation et sur la page publique /sessions. Une place est décomptée à chaque contrat/convention signé.</CardDescription></CardHeader>
        <CardContent>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="session-form">
            <div className="md:col-span-2"><Label>Formation</Label><select required className={selectCls} value={form.formation_code} onChange={(e) => set("formation_code", e.target.value)} data-testid="session-formation"><option value="">— Choisir —</option>{presentiel.map((f) => <option key={f.code} value={f.code}>{f.code} — {f.title}</option>)}</select></div>
            <div><Label>Début</Label><DatePickerFr required value={form.date_debut} onChange={(v) => set("date_debut", v)} data-testid="session-debut" /></div>
            <div><Label>Fin</Label><DatePickerFr required value={form.date_fin} onChange={(v) => set("date_fin", v)} data-testid="session-fin" /></div>
            <div className="md:col-span-2"><Label>Lieu</Label><Input value={form.lieu} onChange={(e) => set("lieu", e.target.value)} data-testid="session-lieu" /></div>
            <div><Label>Horaires</Label><Input value={form.horaires} onChange={(e) => set("horaires", e.target.value)} data-testid="session-horaires" /></div>
            <div><Label>Places</Label><Input type="number" min="1" value={form.places} onChange={(e) => set("places", e.target.value)} data-testid="session-places" /></div>
            <div><Label>Statut</Label><select className={selectCls} value={form.statut} onChange={(e) => set("statut", e.target.value)} data-testid="session-statut"><option value="ouverte">Ouverte</option><option value="annulee">Annulée</option></select></div>
            <div className="md:col-span-3 flex items-end gap-2"><Button type="submit" className="bg-[#F07B1D] hover:bg-[#D96A0F] text-white font-bold" data-testid="session-submit"><Plus className="h-4 w-4 mr-1" /> {editing ? "Enregistrer" : "Créer la session"}</Button>{editing && <Button type="button" variant="ghost" onClick={() => { setEditing(null); setForm(EMPTY_SESSION); }}>Annuler</Button>}</div>
          </form>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-[#003087]">Sessions ({items.length})</CardTitle></CardHeader>
        <CardContent>
          {items.length === 0 ? <p className="text-sm text-gray-500" data-testid="sessions-empty">Aucune session.</p> : (
            <div className="space-y-2">
              {items.map((s) => (
                <div key={s.id} className="flex flex-col md:flex-row md:items-center gap-2 p-3 rounded-lg border border-gray-200" data-testid={`admin-session-${s.id}`}>
                  <Badge className={s.statut === "ouverte" ? "bg-emerald-600 text-white" : s.statut === "complete" ? "bg-red-600 text-white" : "bg-gray-400 text-white"}>{s.statut}</Badge>
                  <p className="text-sm flex-1"><span className="font-semibold text-[#003087]">{s.formation_code} — {s.formation_title}</span><span className="text-gray-600"> · du {frDate(s.date_debut)} au {frDate(s.date_fin)} · {s.lieu}</span></p>
                  <span className="text-xs font-semibold">{s.inscrits}/{s.places} inscrits</span>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => edit(s)} data-testid={`session-edit-${s.id}`}>Modifier</Button>
                  <Button size="sm" variant="ghost" className="h-7 text-red-600" onClick={() => remove(s.id)} data-testid={`session-delete-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const DossiersSection = () => {
  const config = usePilotageConfig();
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("tous");

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/dossiers`, { headers: adminHeaders() });
      setDossiers(res.data);
    } catch {
      toast.error("Impossible de charger les dossiers");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const replace = (d) => setDossiers((list) => list.map((x) => (x.id === d.id ? d : x)));
  const onToggle = async (id, step, done) => {
    try {
      const res = await axios.patch(`${API}/admin/dossiers/${id}/steps/${step}`, { done }, { headers: adminHeaders() });
      replace(res.data);
    } catch {
      toast.error("Mise à jour impossible");
    }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Supprimer ce dossier ?")) return;
    await axios.delete(`${API}/admin/dossiers/${id}`, { headers: adminHeaders() });
    setDossiers((list) => list.filter((x) => x.id !== id));
    toast.success("Dossier supprimé");
  };
  const onDoc = async (d, type) => {
    await downloadWithAuth(`${API}/admin/dossiers/${d.id}/${type}.pdf`, `MGB-School_${type}_${d.nom}.pdf`);
    load(true);
  };
  const [sending, setSending] = useState(null);
  const onSend = async (d, type) => {
    if (!window.confirm(`Envoyer ce document à ${d.email} ?`)) return;
    setSending(type);
    try {
      const res = await axios.post(`${API}/admin/dossiers/${d.id}/send/${type}`, {}, { headers: adminHeaders() });
      toast.success(`Envoyé à ${res.data.sent_to}`);
      load(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Envoi impossible");
    } finally {
      setSending(null);
    }
  };

  const onAction = async (d, kind, arg) => {
    try {
      if (kind === "enquete") {
        if (!window.confirm(`Envoyer l'enquête à ${d.email} ?`)) return;
        await axios.post(`${API}/admin/dossiers/${d.id}/enquete/${arg}`, {}, { headers: adminHeaders() });
        toast.success("Enquête envoyée");
      } else if (kind === "paiement") {
        await axios.post(`${API}/admin/dossiers/${d.id}/paiement`, { paid: arg }, { headers: adminHeaders() });
        toast.success(arg ? "Facture marquée réglée" : "Règlement annulé");
      } else if (kind === "signature") {
        if (!window.confirm(`Envoyer le lien de signature électronique à ${d.email} ?`)) return;
        const res = await axios.post(`${API}/admin/dossiers/${d.id}/signature-link`, {}, { headers: adminHeaders() });
        toast.success(`Lien envoyé : ${res.data.link}`);
      } else if (kind === "paylink") {
        const mode = window.confirm("OK = acompte 30 % · Annuler = totalité / solde") ? "acompte" : "total";
        const res = await axios.post(`${API}/admin/dossiers/${d.id}/paiement-link`, { mode }, { headers: adminHeaders() });
        await navigator.clipboard?.writeText(res.data.link).catch(() => {});
        toast.success(`Lien de paiement ${eur(res.data.amount)} ${res.data.sent ? "envoyé par e-mail" : "créé"} (copié) : ${res.data.link}`);
      } else if (kind === "relance-devis") {
        if (!window.confirm(`Relancer ${d.email} pour la signature du devis ${d.numero_devis || ""} ?`)) return;
        const res = await axios.post(`${API}/admin/dossiers/${d.id}/relance-devis`, {}, { headers: adminHeaders() });
        toast.success(`Relance devis n°${res.data.niveau} envoyée`);
      } else if (kind === "relance") {
        if (!window.confirm(`Envoyer une relance à ${d.email} ?`)) return;
        const res = await axios.post(`${API}/admin/dossiers/${d.id}/relance`, {}, { headers: adminHeaders() });
        toast.success(`Relance ${res.data.niveau} envoyée`);
      }
      load(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action impossible");
    }
  };

  const visible = filter === "tous" ? dossiers : dossiers.filter((d) => d.type_client === filter);
  if (!config) return <p className="text-gray-500 text-sm">Chargement…</p>;

  return (
    <div className="space-y-6">
      <CommandesBlock />
      <Card>
        <CardHeader>
          <CardTitle className="text-[#003087]">Nouveau dossier stagiaire</CardTitle>
          <CardDescription>Chaque dossier suit le parcours administratif (12 étapes) et alimente automatiquement le BPF.</CardDescription>
        </CardHeader>
        <CardContent><DossierForm config={config} onCreated={(d) => setDossiers((l) => [d, ...l])} /></CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <CardTitle className="text-[#003087]">Dossiers ({visible.length})</CardTitle>
              <CardDescription>Cochez chaque étape réalisée ; les documents à produire/conserver sont rappelés à chaque étape. Les convocations sont envoyées automatiquement entre J-15 et J-7 aux dossiers disposant d'un e-mail.</CardDescription>
            </div>
            <div className="flex gap-2 items-center">
              <select className={`${selectCls} w-auto`} value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="dossier-filter">
                <option value="tous">Tous</option>
                <option value="entreprise">Entreprises</option>
                <option value="particulier">Particuliers</option>
              </select>
              <Button size="sm" variant="outline" onClick={load} data-testid="dossiers-refresh"><RefreshCw className="h-4 w-4" /></Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? <p className="text-gray-500 text-sm">Chargement…</p> : visible.length === 0 ? (
            <p className="text-gray-500 text-sm" data-testid="dossiers-empty">Aucun dossier. Créez le premier ci-dessus.</p>
          ) : (
            <div className="space-y-3">
              {visible.map((d) => <DossierCard key={d.id} dossier={d} config={config} onToggle={onToggle} onDelete={onDelete} onDoc={onDoc} onSend={onSend} sending={sending} onAction={onAction} />)}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ==================================================================
// BPF — synthèse Bilan Pédagogique et Financier
// ==================================================================
const BpfTable = ({ title, rows, testId }) => (
  <Card data-testid={testId}>
    <CardHeader className="pb-2"><CardTitle className="text-base text-[#003087]">{title}</CardTitle></CardHeader>
    <CardContent>
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs uppercase text-gray-500 border-b"><th className="py-1.5">Catégorie</th><th className="py-1.5 text-right">Stagiaires</th><th className="py-1.5 text-right">Heures</th><th className="py-1.5 text-right">CA HT</th></tr></thead>
        <tbody>
          {Object.entries(rows).map(([k, r]) => (
            <tr key={k} className="border-b last:border-0"><td className="py-1.5">{r.label}</td><td className="py-1.5 text-right font-medium">{r.stagiaires}</td><td className="py-1.5 text-right">{r.heures}</td><td className="py-1.5 text-right">{eur(r.ca_ht)}</td></tr>
          ))}
          {Object.keys(rows).length === 0 && <tr><td colSpan={4} className="py-2 text-gray-400">Aucune donnée</td></tr>}
        </tbody>
      </table>
    </CardContent>
  </Card>
);

const SatisfactionBlock = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/satisfaction/summary`, { headers: adminHeaders() }).then((r) => setStats(r.data)).catch(() => {});
  }, []);
  if (!stats) return null;
  return (
    <Card data-testid="satisfaction-block">
      <CardHeader className="pb-2">
        <CardTitle className="text-base text-[#003087]">Satisfaction des stagiaires (Qualiopi ind. 2 & 30)</CardTitle>
        <CardDescription>Enquêtes à chaud (clôture) et à froid (J+30). Résultats publiables sur le site dès 3 réponses.</CardDescription>
      </CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(stats).map(([kind, k]) => (
          <div key={kind} className="rounded-xl border border-gray-200 p-4 bg-[#F8FAFC]" data-testid={`satisfaction-${kind}`}>
            <p className="font-bold text-[#003087] text-sm mb-2">{kind === "chaud" ? "À chaud" : "À froid (J+30)"}</p>
            <div className="grid grid-cols-3 gap-2 text-center mb-3">
              <div><p className="text-[10px] uppercase text-gray-500">Réponses</p><p className="font-heading font-extrabold text-lg text-[#003087]">{k.repondus}/{k.envoyes}</p></div>
              <div><p className="text-[10px] uppercase text-gray-500">Moyenne</p><p className="font-heading font-extrabold text-lg text-[#003087]">{k.moyenne_globale ?? "—"}<span className="text-xs font-normal">/5</span></p></div>
              <div><p className="text-[10px] uppercase text-gray-500">NPS</p><p className="font-heading font-extrabold text-lg text-[#003087]">{k.nps ?? "—"}</p></div>
            </div>
            <ul className="text-xs text-gray-700 space-y-0.5">
              {Object.values(k.par_question).map((q) => <li key={q.label} className="flex justify-between gap-2"><span className="truncate">{q.label}</span><span className="font-semibold shrink-0">{q.moyenne ?? "—"}/5</span></li>)}
            </ul>
            {k.temoignages.length > 0 && <p className="text-[11px] text-green-700 mt-2">{k.temoignages.length} témoignage(s) publiable(s) avec accord.</p>}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

const EMPTY_REC = { date: new Date().toISOString().slice(0, 10), source: "stagiaire", objet: "", description: "", traitement: "", statut: "ouverte" };

const ReclamationsBlock = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_REC);
  const load = useCallback(() => axios.get(`${API}/admin/reclamations`, { headers: adminHeaders() }).then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/reclamations`, form, { headers: adminHeaders() });
      toast.success("Réclamation enregistrée");
      setForm(EMPTY_REC);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Enregistrement impossible");
    }
  };
  const close = async (r) => {
    await axios.put(`${API}/admin/reclamations/${r.id}`, { ...r, statut: r.statut === "traitee" ? "ouverte" : "traitee" }, { headers: adminHeaders() });
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer cette réclamation ?")) return;
    await axios.delete(`${API}/admin/reclamations/${id}`, { headers: adminHeaders() });
    load();
  };
  return (
    <Card data-testid="reclamations-block">
      <CardHeader className="pb-2">
        <CardTitle className="text-base text-[#003087]">Registre des réclamations et difficultés (Qualiopi ind. 31-32)</CardTitle>
        <CardDescription>Tracez chaque réclamation, son traitement et la mesure d'amélioration associée — repris dans le rapport annuel.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-6 gap-2" data-testid="reclamation-form">
          <DatePickerFr value={form.date} onChange={(v) => set("date", v)} required data-testid="rec-date" />
          <select className={selectCls} value={form.source} onChange={(e) => set("source", e.target.value)} data-testid="rec-source">
            {["stagiaire", "entreprise", "financeur", "formatrice", "autre"].map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
          <Input className="md:col-span-2" placeholder="Objet de la réclamation" value={form.objet} onChange={(e) => set("objet", e.target.value)} required data-testid="rec-objet" />
          <Input className="md:col-span-2" placeholder="Traitement / mesure d'amélioration" value={form.traitement} onChange={(e) => set("traitement", e.target.value)} data-testid="rec-traitement" />
          <div className="md:col-span-6"><Button type="submit" size="sm" className="bg-[#003087] hover:bg-[#002266] text-white" data-testid="rec-submit"><Plus className="h-4 w-4 mr-1" /> Enregistrer</Button></div>
        </form>
        {items.length === 0 ? <p className="text-sm text-gray-500" data-testid="rec-empty">Aucune réclamation enregistrée.</p> : (
          <div className="space-y-2">
            {items.map((r) => (
              <div key={r.id} className="flex flex-col md:flex-row md:items-center gap-2 p-2.5 rounded-lg border border-gray-200 bg-white" data-testid={`rec-${r.id}`}>
                <Badge className={r.statut === "traitee" ? "bg-green-600 text-white" : "bg-amber-500 text-white"}>{r.statut === "traitee" ? "Traitée" : "Ouverte"}</Badge>
                <p className="text-sm flex-1"><span className="text-gray-500">{new Date(r.date).toLocaleDateString("fr-FR")} · {r.source} — </span><span className="font-semibold text-[#003087]">{r.objet}</span>{r.traitement && <span className="text-gray-600"> → {r.traitement}</span>}</p>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => close(r)} data-testid={`rec-toggle-${r.id}`}>{r.statut === "traitee" ? "Rouvrir" : "Marquer traitée"}</Button>
                <Button size="sm" variant="ghost" className="h-7 text-red-600" onClick={() => remove(r.id)} data-testid={`rec-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const BpfSection = () => {
  const config = usePilotageConfig();
  const [year, setYear] = useState(null);
  const [data, setData] = useState(null);

  const load = useCallback(async (y) => {
    try {
      const res = await axios.get(`${API}/admin/bpf/summary`, { params: y ? { year: y } : {}, headers: adminHeaders() });
      setData(res.data);
      setYear(res.data.year);
    } catch {
      toast.error("Synthèse BPF indisponible");
    }
  }, []);
  useEffect(() => { load(null); }, [load]);

  const kpis = useMemo(() => data ? [
    ["Dossiers ouverts", data.totaux.dossiers],
    ["Stagiaires formés", data.totaux.stagiaires_formes],
    ["Heures-stagiaires", data.totaux.heures_stagiaires],
    ["Heures formatrice", data.totaux.heures_formatrice],
    ["CA HT réalisé", eur(data.totaux.ca_ht_realise)],
    ["CA HT facturé", eur(data.totaux.ca_ht_facture)],
  ] : [], [data]);

  if (!data || !config) return <p className="text-gray-500 text-sm">Chargement…</p>;

  return (
    <div className="space-y-6" data-testid="bpf-section">
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <CardTitle className="text-[#003087]">Bilan Pédagogique et Financier — préparation {data.year}</CardTitle>
              <CardDescription>Déclaration annuelle obligatoire (art. L6352-11 C. trav.) à transmettre avant le 30 avril N+1 sur « Mon Activité Formation ». Un stagiaire est compté « formé » dès l'étape « Début de formation » cochée.</CardDescription>
            </div>
            <div className="flex gap-2 items-center">
              <select className={`${selectCls} w-auto`} value={year || ""} onChange={(e) => load(Number(e.target.value))} data-testid="bpf-year">
                {data.years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
              <Button onClick={() => downloadWithAuth(`${API}/admin/bpf/export.csv?year=${year}`, `MGB-School_BPF_${year}.csv`)} className="bg-[#F07B1D] hover:bg-[#D96A0F] text-white font-bold" data-testid="bpf-export-csv">
                <Download className="h-4 w-4 mr-1" /> Export CSV
              </Button>
              <Button onClick={() => downloadWithAuth(`${API}/admin/rapport-qualiopi.pdf?year=${year}`, `MGB-School_Rapport-Qualiopi_${year}.pdf`)} className="bg-[#003087] hover:bg-[#002266] text-white font-bold" data-testid="rapport-qualiopi-btn">
                <FileText className="h-4 w-4 mr-1" /> Rapport annuel PDF
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {kpis.map(([label, value]) => (
              <div key={label} className="rounded-xl border border-gray-200 bg-[#F8FAFC] p-4" data-testid={`bpf-kpi-${label}`}>
                <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
                <p className="font-heading text-xl font-extrabold text-[#003087] mt-1">{value}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <BpfTable title="Cadre C — Origine des produits (financeurs)" rows={data.cadre_c_origine_produits} testId="bpf-cadre-c" />
        <BpfTable title="Cadre F — Typologie des stagiaires" rows={data.cadre_f_typologie_stagiaires} testId="bpf-cadre-f" />
        <BpfTable title="Modalités pédagogiques" rows={data.cadre_f_modalites} testId="bpf-modalites" />
        <BpfTable title="Cadre G — Spécialités / formations dispensées" rows={data.cadre_g_specialites} testId="bpf-cadre-g" />
      </div>

      <SatisfactionBlock />

      <ReclamationsBlock />

      <Card data-testid="bpf-pipeline">
        <CardHeader className="pb-2"><CardTitle className="text-base text-[#003087]">Avancement des dossiers {data.year} (dernière étape atteinte)</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {Object.entries(config.parcours).map(([type, steps]) => (
            <div key={type}>
              <p className="text-sm font-bold text-[#003087] mb-2">{type === "entreprise" ? "Parcours Entreprise" : "Parcours Particulier"}</p>
              <div className="flex flex-wrap gap-1.5">
                {steps.map((s, i) => {
                  const n = data.pipeline[type]?.[s.key] || 0;
                  return <span key={s.key} className={`text-xs px-2 py-1 rounded-md border ${n ? "bg-[#003087] text-white border-[#003087]" : "bg-white text-gray-500 border-gray-200"}`}>{i + 1}. {s.label} {n ? `(${n})` : ""}</span>;
                })}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

// ==================================================================
// CHECKLIST QUALIOPI — 32 indicateurs
// ==================================================================
const STATUT_STYLE = {
  a_faire: ["À faire", "bg-gray-100 text-gray-700"],
  en_cours: ["En cours", "bg-amber-100 text-amber-800"],
  fait: ["Fait", "bg-green-100 text-green-800"],
  non_applicable: ["Non applicable", "bg-slate-100 text-slate-500"],
};

const ChecklistRow = ({ item, onSave }) => {
  const [statut, setStatut] = useState(item.statut);
  const [preuve, setPreuve] = useState(item.preuve || "");
  const [note, setNote] = useState(item.note || "");
  const dirty = statut !== item.statut || preuve !== (item.preuve || "") || note !== (item.note || "");
  return (
    <div className={`p-3 rounded-lg border ${statut === "fait" ? "border-green-200 bg-green-50/40" : "border-gray-200 bg-white"}`} data-testid={`checklist-item-${item.indicateur}`}>
      <div className="flex flex-col md:flex-row md:items-start gap-3">
        <div className="flex-1">
          <p className="text-sm font-semibold text-[#003087]">Indicateur {item.indicateur} <span className={`ml-2 text-[11px] px-2 py-0.5 rounded-full ${STATUT_STYLE[statut][1]}`}>{STATUT_STYLE[statut][0]}</span></p>
          <p className="text-sm text-gray-700">{item.label}</p>
        </div>
        <select className={`${selectCls} md:w-44`} value={statut} onChange={(e) => setStatut(e.target.value)} data-testid={`checklist-statut-${item.indicateur}`}>
          {Object.entries(STATUT_STYLE).map(([k, [lab]]) => <option key={k} value={k}>{lab}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
        <Input placeholder="Preuves / documents (ex. Doc 01, site web, registre…)" value={preuve} onChange={(e) => setPreuve(e.target.value)} data-testid={`checklist-preuve-${item.indicateur}`} />
        <Input placeholder="Note / action à mener" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`checklist-note-${item.indicateur}`} />
      </div>
      {dirty && (
        <div className="mt-2 flex justify-end">
          <Button size="sm" className="bg-[#003087] hover:bg-[#002266] text-white" onClick={() => onSave(item.indicateur, { statut, preuve, note })} data-testid={`checklist-save-${item.indicateur}`}>Enregistrer</Button>
        </div>
      )}
    </div>
  );
};

export const ChecklistSection = () => {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/qualiopi/checklist`, { headers: adminHeaders() });
      setData(res.data);
    } catch {
      toast.error("Checklist indisponible");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onSave = async (indicateur, payload) => {
    try {
      await axios.put(`${API}/admin/qualiopi/checklist/${indicateur}`, payload, { headers: adminHeaders() });
      toast.success(`Indicateur ${indicateur} enregistré`);
      load();
    } catch {
      toast.error("Enregistrement impossible");
    }
  };

  if (!data) return <p className="text-gray-500 text-sm">Chargement…</p>;
  const byCritere = Object.entries(data.criteres).map(([num, label]) => ({ num: Number(num), label, items: data.items.filter((i) => i.critere === Number(num)) }));

  return (
    <div className="space-y-6" data-testid="checklist-section">
      <Card>
        <CardHeader>
          <CardTitle className="text-[#003087]">Progression Qualiopi — Référentiel National Qualité (7 critères, 32 indicateurs)</CardTitle>
          <CardDescription>Les indicateurs propres à l'apprentissage / aux certifications sont pré-marqués « non applicable ». Renseignez les preuves pour préparer l'audit initial.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="flex-1 h-4 rounded-full bg-gray-100 overflow-hidden"><div className="h-full bg-green-500 transition-all" style={{ width: `${data.progress.pourcentage}%` }} /></div>
            <p className="font-heading font-extrabold text-[#003087] text-xl" data-testid="checklist-progress">{data.progress.pourcentage}%</p>
          </div>
          <p className="text-xs text-gray-500 mt-2">{data.progress.fait} fait(s) · {data.progress.en_cours} en cours · {data.progress.applicable} indicateurs applicables</p>
        </CardContent>
      </Card>
      {byCritere.map((c) => (
        <Card key={c.num}>
          <CardHeader className="pb-2"><CardTitle className="text-base text-[#003087]"><FileText className="inline h-4 w-4 mr-1" />Critère {c.num} — {c.label}</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {c.items.map((i) => <ChecklistRow key={`${i.indicateur}-${i.updated_at}`} item={i} onSave={onSave} />)}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
